<template>
  <div>
    <div class="flex flex-wrap items-center justify-between gap-3 mb-6">
      <div>
        <h3 class="text-lg font-semibold">Email performance</h3>
        <p class="text-sm text-gray-600">See how recipients engage with each type of email. Open figures are estimates because some email apps load images automatically.</p>
      </div>
      <div class="flex flex-wrap gap-2">
        <TimeRangePresetButtons
          :presets="presets"
          :active-preset="activePreset"
          :all-time="allTime"
          include-all
          @apply-preset="$emit('apply-preset', $event)"
          @load-all="$emit('load-all')"
        />
        <button class="px-3 py-2 rounded border text-sm bg-white hover:bg-gray-50" :disabled="loading" @click="$emit('refresh')">
          Refresh
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-sm text-gray-500 py-8">Loading email engagement…</div>
    <div v-else-if="error" class="text-sm text-red-600 py-6">
      We couldn't load the email performance data.
      <button class="ml-2 underline" @click="$emit('refresh')">Retry</button>
    </div>
    <div v-else-if="stats">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div v-for="card in cards" :key="card.label" class="rounded-xl p-5 border bg-white">
          <div class="text-xs uppercase tracking-wide text-gray-500 mb-2">{{ card.label }}</div>
          <div class="text-2xl font-semibold">{{ card.value }}</div>
          <div class="text-xs text-gray-500 mt-1">{{ card.detail }}</div>
        </div>
      </div>

      <div class="max-w-sm mb-6">
        <div>
          <label class="block text-sm text-gray-600 mb-1">Ceremony table season</label>
          <select v-model="season" class="border rounded px-3 py-2 w-full">
            <option value="all">All seasons</option>
            <option v-for="row in seasonRows" :key="row.season" :value="String(row.season)">{{ seasonLabel(row.season) }}</option>
          </select>
        </div>
      </div>

      <div class="overflow-x-auto mb-8">
        <h4 class="font-semibold mb-3">By email type</h4>
        <table class="min-w-full border-collapse text-sm">
          <thead><tr class="border-b text-left text-gray-500"><th class="py-2 pr-4">Email</th><th class="py-2 px-3">Sent</th><th class="py-2 px-3">Delivered</th><th class="py-2 px-3">Open rate</th><th class="py-2 px-3">Click rate</th><th class="py-2 px-3">Bounced</th></tr></thead>
          <tbody>
            <tr v-for="row in messageRows" :key="row.message_type" class="border-b">
              <td class="py-3 pr-4 font-medium">{{ messageTypeLabel(row.message_type) }}</td>
              <td class="py-3 px-3">{{ row.send }}</td><td class="py-3 px-3">{{ row.delivery }}</td><td class="py-3 px-3">{{ percent(row.unique_open_rate) }}</td><td class="py-3 px-3">{{ percent(row.unique_click_rate) }}</td><td class="py-3 px-3">{{ row.bounce }}</td>
            </tr>
            <tr v-if="messageRows.length === 0"><td colspan="6" class="py-4 text-gray-500">No email activity for the selected period.</td></tr>
          </tbody>
        </table>
      </div>

      <div class="overflow-x-auto">
        <h4 class="font-semibold mb-3">By ceremony</h4>
        <table class="min-w-full border-collapse text-sm">
          <thead><tr class="border-b text-left text-gray-500"><th class="py-2 pr-4">Ceremony</th><th class="py-2 px-3">Season</th><th class="py-2 px-3">Sent</th><th class="py-2 px-3">Delivered</th><th class="py-2 px-3">Open rate</th><th class="py-2 px-3">Click rate</th></tr></thead>
          <tbody>
            <tr v-for="row in filteredCeremonyRows" :key="row.ceremony_id || 'unmapped'" class="border-b">
              <td class="py-3 pr-4">
                <div class="font-medium">{{ ceremonyDateTime(row) }}</div>
                <div v-if="row.ceremony_datetime && row.ceremony_label" class="text-xs text-gray-500 mt-1">{{ row.ceremony_label }}</div>
              </td><td class="py-3 px-3">{{ seasonLabel(row.season) }}</td><td class="py-3 px-3">{{ row.send }}</td><td class="py-3 px-3">{{ row.delivery }}</td><td class="py-3 px-3">{{ percent(row.unique_open_rate) }}</td><td class="py-3 px-3">{{ percent(row.unique_click_rate) }}</td>
            </tr>
            <tr v-if="filteredCeremonyRows.length === 0"><td colspan="6" class="py-4 text-gray-500">No ceremonies match the selected filters.</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import TimeRangePresetButtons from '../shared/TimeRangePresetButtons.vue'

export default {
  name: 'EmailEngagementPanel',
  components: { TimeRangePresetButtons },
  props: {
    stats: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    presets: { type: Array, default: () => [] },
    activePreset: { type: String, default: '' },
    allTime: { type: Boolean, default: false },
  },
  data() {
    return { season: 'all' }
  },
  computed: {
    summary() { return this.stats?.summary || {} },
    messageRows() { return Array.isArray(this.stats?.by_message_type) ? this.stats.by_message_type : [] },
    seasonRows() { return Array.isArray(this.stats?.by_season) ? this.stats.by_season : [] },
    ceremonyRows() { return Array.isArray(this.stats?.by_ceremony) ? this.stats.by_ceremony : [] },
    filteredCeremonyRows() {
      return this.ceremonyRows.filter(row => {
        const seasonMatches = this.season === 'all' || String(row.season ?? 'unmapped') === this.season
        return seasonMatches
      })
    },
    cards() {
      return [
        { label: 'Sent', value: this.summary.send || 0, detail: 'Individual emails sent' },
        { label: 'Delivered', value: this.summary.delivery || 0, detail: `${this.percent(this.summary.delivery_rate)} delivery rate` },
        { label: 'Opened', value: this.summary.open || 0, detail: `${this.percent(this.summary.unique_open_rate)} unique open rate` },
        { label: 'Clicked', value: this.summary.click || 0, detail: `${this.percent(this.summary.unique_click_rate)} unique click rate` },
      ]
    },
  },
  methods: {
    percent(value) { return `${Number(value || 0).toFixed(1)}%` },
    messageTypeLabel(value) { return String(value || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()) },
    ceremonyDateTime(row) {
      if (!row?.ceremony_datetime) return row?.ceremony_label || 'Ceremony date not available'
      const date = new Date(row.ceremony_datetime)
      if (Number.isNaN(date.getTime())) return row.ceremony_label || 'Ceremony date not available'
      return new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(date)
    },
    seasonLabel(value) { return String(value ?? 'unmapped') === 'unmapped' ? 'Not assigned' : String(value) },
  },
}
</script>
